import { Copy01Icon, Download01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useRef } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { QRCodeCanvas } from "qrcode.react";
import useSnackbar from "@/hooks/useSnackbar";
import { Survey } from "@/types/surveys";

interface SurveyShareDialogProps {
  open: boolean;
  onClose: () => void;
  survey: Survey | null;
  organizationAlias?: string;
  organizationId?: string;
}

const SurveyShareDialog = ({
  open,
  onClose,
  survey,
  organizationAlias,
  organizationId,
}: SurveyShareDialogProps) => {
  const [openSnackbar] = useSnackbar();
  const canvasRef = useRef<HTMLDivElement>(null);

  if (!survey) return null;

  const basePath = organizationAlias
    ? `${window.location.origin}/${organizationAlias}/surveys/${survey.id}`
    : `${window.location.origin}/comercios/${organizationId}/surveys/${survey.id}/submit`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(basePath);
      openSnackbar("Enlace copiado al portapapeles", "success");
    } catch {
      openSnackbar("No se pudo copiar el enlace", "error");
    }
  };

  const handleDownloadQR = () => {
    const canvas = canvasRef.current?.querySelector("canvas");
    if (!canvas) return;
    const imgFile = canvas.toDataURL("image/png");
    const downloadLink = document.createElement("a");
    downloadLink.download = `QR-Encuesta-${survey.title.replace(/\s+/g, "-")}.png`;
    downloadLink.href = imgFile;
    downloadLink.click();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Compartir Encuesta</DialogTitle>
      <DialogContent>
        <Stack spacing={3} alignItems="center" pt={1}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Comparte este enlace o descarga el código QR para tus mesas, empaques de delivery o redes sociales.
          </Typography>

          <div ref={canvasRef}>
            <QRCodeCanvas
              value={basePath}
              size={200}
              bgColor="#ffffff"
              fgColor="#000000"
              level="M"
              includeMargin
            />
          </div>

          <TextField
            fullWidth
            size="small"
            label="Enlace público"
            value={basePath}
            slotProps={{
              input: {
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Copiar enlace">
                      <IconButton onClick={handleCopyLink} edge="end" size="small">
                        <HugeiconsIcon icon={Copy01Icon} size={18} />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cerrar</Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<HugeiconsIcon icon={Download01Icon} />}
          onClick={handleDownloadQR}
        >
          Descargar QR
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SurveyShareDialog;
